import { Component, inject } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon, MatIconModule } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import {
  OptionSelect,
  SearchSelect,
} from '../../../../shared/components/search-select/search-select';
import { Employee, EmployeeDTO } from '../../../../core/models/employee.interface';
import { Expense } from '../../../../core/models/expense.interface';
import { MatDialogRef } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { LoadingService } from '../../../../core/services/loading/loading';
import { ExpensesService } from '../../../../core/services/expenses/expenses-service';
import { EmployeeService } from '../../../../core/services/employee/employee-service';
import { FormUtils } from '../../../../shared/utils';
import { switchMap } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { MatCheckboxModule } from '@angular/material/checkbox';

@Component({
  selector: 'app-employee-form',
  imports: [
    MatFormField,
    ReactiveFormsModule,
    MatLabel,
    MatInput,
    MatError,
    MatIcon,
    MatIconModule,
    FormsModule,
    MatCheckboxModule,
    SearchSelect,
  ],
  templateUrl: './employee-form.html',
  styleUrl: './employee-form.scss',
})
export class EmployeeForm {
  employee: string | null = null;
  errorMsg: string[] | null = null;
  employeeSelected: Employee | null = null;
  expenseSelected: OptionSelect[] = [];
  listExpenses: Expense[] = [];
  selectExpense: OptionSelect | null = null;
  expenseSeleccionado: Expense | null = null;
  private formBuilder = inject(FormBuilder);

  constructor(
    private dialogRef: MatDialogRef<EmployeeForm>,
    private toastr: ToastrService,
    private serviceEmployee: EmployeeService,
    private loadingService: LoadingService,
    private expensesService: ExpensesService,
  ) {}

  ngOnInit(): void {
    this.fetchEmployee();
    this.serviceEmployee.asyncErrorValidation$.subscribe((error) => {
      if (error) {
        this.errorMsg = FormUtils.parseErrorMsgs(error);
      }
    });
  }

  private fetchEmployee() {
    this.expensesService
      .getExpense()
      .pipe(
        switchMap((expenses) => {
          this.listExpenses = expenses;
          this.expenseSelected = this.expensesService.getOptionExpense(expenses);
          return this.serviceEmployee.selectedEmployee$;
        }),
      )
      .subscribe((employee) => {
        if (employee) {
          this.employee = String(employee.id);
          this.employeeSelected = employee;
          this.employeeForm.patchValue({
            ...this.employeeSelected,
            expense: this.employeeSelected.expense?.id || 0,
          });
          const expenseOpt = this.expenseSelected.find((el) => {
            if (this.employeeSelected) {
              return el.value === this.employeeSelected.expense?.id;
            }
            return false;
          });
          if (typeof expenseOpt !== 'undefined') {
            this.selectExpense = expenseOpt;
          }
        }
      });
  }

  employeeForm = this.formBuilder.group({
    name: ['', [Validators.required, Validators.maxLength(256)]],
    relationshipType: ['', [Validators.maxLength(256)]],
    description: ['', [Validators.required]],
    communityExperience: ['', [Validators.required]],
    academicExperience: ['', [Validators.required]],
    functions: ['', [Validators.maxLength(256)]],
    experienceTime: [0, [Validators.required]],
    hasMultipleComponents: [false, [Validators.required]],
    hasMultipleLevels: [false, [Validators.required]],
    accompanimenTime: ['', [Validators.maxLength(256)]],
    compensacionType: ['', [Validators.maxLength(100)]],
    monthSalary: [0, []],
    expense: [0, []],
  });

  getExpense(id: number) {
    const index = this.listExpenses.findIndex((expense) => expense.id === id);
    this.expenseSeleccionado = this.listExpenses[index];
  }

  onExpenseSelected(expense: OptionSelect) {
    this.employeeForm.get('expense')?.setValue(expense.value as number);
    this.getExpense(expense.value as number);
  }

  closeDialog() {
    this.dialogRef.close();
  }

  saveEmployee() {
    const payload = this.employeeForm.value as EmployeeDTO;
    if (this.employeeForm.status === 'VALID' && !this.employee) {
      this.loadingService.show(true);
      this.serviceEmployee.createEmployee(payload).subscribe({
        next: (employee) => {
          this.loadingService.show(false);
          const msg = `El empleado ${employee.name} fue CREADO correctamente!`;
          this.toastr.success(msg, 'Exitoso!', {
            progressBar: true,
            timeOut: 5000,
          });
          this.dialogRef.close();
        },
        error: ({}: HttpErrorResponse) => {
          if (this.errorMsg) {
            this.loadingService.show(false);
            this.toastr.error('Error: ' + this.errorMsg, undefined, {
              progressBar: true,
              timeOut: 5000,
            });
          }
        },
      });
    } else if (this.employeeForm.status === 'VALID' && this.employee) {
      this.loadingService.show(true);
      const id = Number(this.employee);
      this.serviceEmployee.updateEmployee(id, { ...payload, id: id }).subscribe({
        next: (employee) => {
          this.loadingService.show(false);
          const msg = `El empleado ${employee.name} fue EDITADO correctamente!`;
          this.toastr.success(msg, 'Exitoso!', {
            progressBar: true,
            timeOut: 5000,
          });
          this.dialogRef.close();
        },
        error: ({}: HttpErrorResponse) => {
          if (this.errorMsg) {
            this.loadingService.show(false);
            this.toastr.error('Error: ' + this.errorMsg, undefined, {
              progressBar: true,
              timeOut: 5000,
            });
          }
        },
      });
    }
  }
  getExpenseValue() {
    return this.selectExpense;
  }
}
